// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import familiarApi from './familiar.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchFamiliarApi = createAsyncThunk(
    'heroSaga/familiar/fetchFamiliarApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await familiarApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleFamiliarPublishedStateApi = createAsyncThunk(
    'heroSaga/familiar/toggleFamiliarPublishedStateApi',
    async (row, { dispatch, rejectWithValue }) => {
        try {
            await familiarApi.update(row.id, {is_ready: !row.is_ready});
            return row.id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createFamiliarApi = createAsyncThunk(
    'heroSaga/familiar/createFamiliarApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await familiarApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeFamiliarApi = createAsyncThunk(
    'heroSaga/familiar/storeFamiliarApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const familiar = await familiarApi.store(values);
            alertSwal('success', 'Familiar created successfully!');

            return familiar;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showFamiliarApi = createAsyncThunk(
    'heroSaga/familiar/showFamiliarApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await familiarApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateFamiliarApi = createAsyncThunk(
    'heroSaga/familiar/updateFamiliarApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await familiarApi.update(id, values);
            alertSwal('success', 'Familiar updated successfully!');

            const { ranks } = state.heroSaga.familiarMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteFamiliarApi = createAsyncThunk('heroSaga/familiar/deleteFamiliarApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await familiarApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const familiarSlice = createSlice({
    name: 'heroSaga/familiar',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedFamiliar(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFamiliarApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchFamiliarApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleFamiliarPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleFamiliarPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createFamiliarApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createFamiliarApi.rejected, () => {
                //
            });

        builder
            .addCase(storeFamiliarApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeFamiliarApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showFamiliarApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showFamiliarApi.rejected, () => {
                //
            });

        builder
            .addCase(updateFamiliarApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateFamiliarApi.rejected, () => {
                //
            });

        builder.addCase(deleteFamiliarApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedFamiliar } = familiarSlice.actions;
